import React, { useState, useEffect } from 'react';
import MenuLateral from '../../Components/MenuPrincipal/MenuLateral';
import { useAuth } from '../../Context/auth';
import './dashboard.css'; // Vamos criar um CSS específico ou usar inline por enquanto/bootstrap

// Chart.js imports
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import Swal from 'sweetalert2';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

function DashboardGerencial() {
    const { currentUser } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filterPeriod, setFilterPeriod] = useState('thisMonth');

    const loadDashboardData = React.useCallback(async () => {
        setLoading(true);
        try {
            const token = await currentUser.getIdToken();
            const API_URL = process.env.REACT_APP_API_URL;
            const res = await fetch(`${API_URL}/api/gerencial/dashboard?period=${filterPeriod}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!res.ok) throw new Error('Falha ao carregar dashboard');
            const result = await res.json();
            setData(result);
        } catch (error) {
            console.error(error);
            Swal.fire('Erro', 'Não foi possível carregar os dados.', 'error');
        } finally {
            setLoading(false);
        }
    }, [currentUser, filterPeriod]);

    useEffect(() => {
        if (currentUser) {
            loadDashboardData();
        }
    }, [currentUser, loadDashboardData]);

    if (loading && !data) return <div className="p-5 text-center">Carregando Dashboard...</div>;
    // Removendo check !data return null para permitir que o refresh aconteça com dados antigos na tela enquanto carrega (melhor UX), ou mantendo loading overlay.
    // Mas o 'setLoading(true)' vai disparar o "Carregando Dashboard..." se eu não mudar a lógica.
    // Melhor manter simples por enquanto.
    if (!data && !loading) return null; // Só retorna null se não tiver dados E não estiver carregando (erro?)

    // Chart Data Config
    const chartOptions = {
        responsive: true,
        plugins: {
            legend: { position: 'top' },
            title: { display: true, text: 'Pedidos Cadastrados (Por Dia)' },
        },
    };

    const labels = data.charts.orders.map(d => d.dia);
    const orderData = data.charts.orders.map(d => parseInt(d.qtd));

    const chartData = {
        labels,
        datasets: [
            {
                label: 'Pedidos',
                data: orderData,
                backgroundColor: 'rgba(53, 162, 235, 0.5)',
            }
        ],
    };

    return (
        <div style={{ display: 'flex', paddingTop: 0, minHeight: '100vh', backgroundColor: '#f4f6f9' }}>
            <MenuLateral />

            <div className="content-with-sidebar" style={{ width: '100%', padding: '20px' }}>
                <div className="dashboard-header">
                    <h1 className="dashboard-title">Dashboard Gerencial</h1>
                    <div className="d-flex gap-3 align-items-center">
                        <div className="d-flex align-items-center">
                            <label className="me-2 fw-bold text-muted mb-0">Período:</label>
                            <select
                                className="form-select w-auto"
                                value={filterPeriod}
                                onChange={(e) => setFilterPeriod(e.target.value)}
                            >
                                <option value="hoje">Hoje</option>
                                <option value="thisWeek">Esta Semana</option>
                                <option value="thisMonth">Este Mês</option>
                                <option value="thisYear">Este Ano</option>
                            </select>
                        </div>
                        <button className="btn btn-primary" onClick={loadDashboardData} disabled={loading} title="Atualizar dados">
                            <i className={`fas fa-sync-alt me-2 ${loading ? 'fa-spin' : ''}`}></i> Atualizar
                        </button>
                    </div>
                </div>

                {loading && <div className="text-center mb-3"><small className="text-muted">Atualizando...</small></div>}

                {/* Cards Linha 1: Tenants */}
                <div className="row g-3 mb-4">
                    <div className="col-md-3">
                        <div className="card shadow-sm border-0 h-100 p-3 text-center">
                            <h5 className="text-muted">Tenants Ativos</h5>
                            <h2 className="text-success fw-bold">{data.tenants.ativos}</h2>
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="card shadow-sm border-0 h-100 p-3 text-center">
                            <h5 className="text-muted">Em Teste</h5>
                            <h2 className="text-warning fw-bold">{data.tenants.teste}</h2>
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="card shadow-sm border-0 h-100 p-3 text-center">
                            <h5 className="text-muted">Vencidos</h5>
                            <h2 className="text-danger fw-bold">{data.tenants.vencidos}</h2>
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="card shadow-sm border-0 h-100 p-3 text-center">
                            <h5 className="text-muted">Novos ({filterPeriod === 'hoje' ? 'Hoje' : 'No Período'})</h5>
                            <h2 className="text-primary fw-bold">{data.tenants.novos_hoje}</h2>
                        </div>
                    </div>
                </div>

                {/* Cards Linha 2: Financeiro e Totais */}
                <div className="row g-3 mb-4">
                    <div className="col-md-4">
                        <div className="card shadow-sm border-0 h-100 p-3">
                            <h5 className="text-muted mb-3">Faturamento Mensal Previsto</h5>
                            <h2 className="text-dark fw-bold">
                                {parseFloat(data.tenants.faturamento_previsto || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                            </h2>
                            <small className="text-muted">Soma das mensalidades de tenants ativos</small>
                        </div>
                    </div>
                    <div className="col-md-8">
                        <div className="row g-3 h-100">
                            <div className="col-md-4">
                                <div className="card shadow-sm border-0 h-100 p-3 text-center bg-light">
                                    <h6 className="text-muted">Total de Pessoas</h6>
                                    <h3>{data.totals.total_usuarios}</h3>
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="card shadow-sm border-0 h-100 p-3 text-center bg-light">
                                    <h6 className="text-muted">Sessões Ativas</h6>
                                    <h3>{data.totals.total_sessoes}</h3>
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="card shadow-sm border-0 h-100 p-3 text-center bg-light">
                                    <h6 className="text-muted">Total de Pedidos</h6>
                                    <h3>{data.totals.total_pedidos}</h3>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Gráficos */}
                <div className="row g-3">
                    <div className="col-12">
                        <div className="card shadow-sm border-0 p-4">
                            <Bar options={chartOptions} data={chartData} height={80} />
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
}

export default DashboardGerencial;
